import { useState, useEffect, useMemo } from "react";
import { CalendarDate } from "@internationalized/date";
import type { DateValue } from "react-aria-components";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { DateRangePicker } from "@/components/application/date-picker/date-range-picker";
import type { ActivityFilters } from "@/types/activity";

interface Props {
    value: ActivityFilters;
    onChange: (next: ActivityFilters) => void;
}

// Convert Date to CalendarDate
const dateToCalendarDate = (date: Date): CalendarDate => {
    return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

// Convert CalendarDate to Date
const calendarDateToDate = (dateValue: DateValue): Date => {
    return new Date(dateValue.year, dateValue.month - 1, dateValue.day);
};

export const ActivityFiltersBar = ({ value, onChange }: Props) => {
    const [local, setLocal] = useState<ActivityFilters>(value);

    useEffect(() => setLocal(value), [value]);

    const readOptions = [
        { id: "all", label: "Tutte" },
        { id: "unread", label: "Non lette" },
        { id: "read", label: "Lette" },
    ];

    const dateRangeValue = useMemo(() => {
        if (local.fromDate && local.toDate) {
            return {
                start: dateToCalendarDate(local.fromDate),
                end: dateToCalendarDate(local.toDate),
            };
        }
        return null;
    }, [local.fromDate, local.toDate]);

    return (
        <div className="rounded-xl bg-primary p-4 shadow-xs ring-1 ring-secondary">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <Select
                    placeholder="Stato lettura"
                    items={readOptions}
                    selectedKey={(local.readState || "all") as string}
                    onSelectionChange={(key) => {
                        const next = { ...local, readState: key as any };
                        setLocal(next);
                        onChange(next);
                    }}
                    size="md"
                >
                    {(item) => (
                        <Select.Item key={item.id} id={item.id}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>

                <DateRangePicker
                    value={dateRangeValue}
                    onChange={(range) => {
                        const next: ActivityFilters = {
                            ...local,
                            fromDate: range?.start ? calendarDateToDate(range.start) : null,
                            toDate: range?.end ? calendarDateToDate(range.end) : null,
                        };
                        setLocal(next);
                        onChange(next);
                    }}
                />

                <Input
                    placeholder="Cerca per keyword..."
                    value={local.keyword || ""}
                    onChange={(v) => {
                        const next = { ...local, keyword: v };
                        setLocal(next);
                        onChange(next);
                    }}
                    size="md"
                />
            </div>
        </div>
    );
};

export default ActivityFiltersBar;


